import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { LazyScreen } from '../LazyScreen/LazyScreen';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { AnimatedImageBox } from '../PhotoTrio/PhotoTrio.styles';
import { MarqueeContainer } from './PhotoMarquee.styles';
import type { MarqueePhotoRendererProps, PhotoMarqueeProps } from './PhotoMarquee.types';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { findAspectRatioFromPercentCrop } from '@shared/utils/findAspectRatioFromPercentCrop';

const PHOTO_COUNT_TO_SHOW = 6;
const MAX_ASPECT_RATIO = 12 / 7;
const PHOTO_HEIGHT = 882;
const MAX_HEIGHT = `calc(100vw / ${MAX_ASPECT_RATIO})`; // Calculate max height based on viewport width

const MarqueePhotoRenderer: FC<MarqueePhotoRendererProps> = props => {
  const { photo, index, containerDimensions } = props;

  if (photo.cropInfo) {
    const aspectRatio = findAspectRatioFromPercentCrop(photo.cropInfo, photo.width, photo.height);
    return (
      <AnimatedImageBox
        key={`${photo.id}-${index}`}
        width={containerDimensions.height * aspectRatio}
        height="100%"
      >
        <CroppedImage
          cropInfo={{ ...photo.cropInfo, unit: '%' }}
          imageUrl={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
          height={containerDimensions.height}
          width={containerDimensions.height * aspectRatio}
        />
      </AnimatedImageBox>
    );
  }
  return (
    <AnimatedImageBox
      key={`${photo.id}-${index}`}
      width={`calc(calc(100vw / ${MAX_ASPECT_RATIO}) * ${photo.width / photo.height})`}
      height="100%"
      backgroundImage={backgroundImageForUrl(addRendition({ url: photo.photoUrl, renditionSize: 'medium' }))}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
    />
  );
};

export const PhotoMarquee: FC<PhotoMarqueeProps> = memo(props => {
  const { photos, showSkeleton, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);
  const marqueePhotos = useMemo(() => [...filledPhotos, ...filledPhotos], [filledPhotos]);

  const [containerDimensions, containerRef] = useSizeObserver();

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Flex
      width="100vw"
      position="relative"
      left="calc(-50vw + 50%)"
      overflow="hidden"
    >
      <MarqueeContainer
        flexDirection="row"
        flexBasis="100%"
        gap={1}
        maxHeight={MAX_HEIGHT}
        height={PHOTO_HEIGHT}
        ref={containerRef}
      >
        {marqueePhotos.map((photo, index) => (
          <LazyScreen
            key={`${photo.id}-${index}`}
            src={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
            width={`calc(calc(100vw / ${MAX_ASPECT_RATIO}) * ${photo.width / photo.height})`}
            height="100%"
            showSkeleton={showSkeleton}
          >
            {() =>
              photo.photoUrl ? (
                <MarqueePhotoRenderer
                  photo={photo}
                  index={index}
                  containerDimensions={containerDimensions}
                />
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  aspectRatio={`${MAX_ASPECT_RATIO}`}
                  backgroundColor="lightgray"
                  key={photo.id}
                />
              )
            }
          </LazyScreen>
        ))}
      </MarqueeContainer>
    </Flex>
  );
});

PhotoMarquee.displayName = 'PhotoMarquee';
